import { useState } from 'react'
import { Eye, LogOut } from 'lucide-react'
import { toast } from 'sonner'
import { api, extractApiError } from '../lib/api'
import { setFlashToast } from '../lib/flash'
import { useAuthStore } from '../stores/authStore'
import { Button } from './ui/Button'

/**
 * Sticky strip across the top of the app whenever the current session
 * is being run by a Super Admin viewing the app "as" another user (§1).
 * Stays mounted across every screen so the admin always knows they're
 * inside someone else's account — impersonated actions are recorded
 * against the impersonated user, not the admin.
 *
 * Powered by /auth/me's `impersonation` payload (resolved server-side
 * from the JWT's `impersonatedBy` claim) and persisted in the auth
 * store. The Exit button calls POST /auth/exit-impersonation which
 * re-issues the admin's original cookies, then we hard-navigate back to
 * the Super Admin panel so React Query caches scoped to the impersonated
 * tenant don't leak into the admin's view.
 */
export function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.impersonation)
  const setImpersonation = useAuthStore((s) => s.setImpersonation)
  const [exiting, setExiting] = useState(false)

  if (!impersonation) return null

  const adminLabel =
    impersonation.adminName || impersonation.adminEmail || 'Super Admin'

  async function handleExit() {
    if (exiting) return
    setExiting(true)
    try {
      await api.post('/auth/exit-impersonation')
      setImpersonation(null)
      // Queue the toast to fire AFTER the reload below — a toast here would be
      // wiped by the hard navigation before it ever paints (which is why no
      // success message showed on exit).
      setFlashToast('Exited impersonation successfully.')
      // Hard navigate so React Query caches scoped to the impersonated
      // tenant don't leak into the admin's view.
      window.location.href = '/super-admin/all-users'
    } catch (err) {
      toast.error(extractApiError(err).message)
      setExiting(false)
    }
  }

  return (
    <div className="w-full bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)] border-b border-[color:var(--color-accent-border)]">
      <div className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Eye size={16} className="shrink-0" />
          <span className="truncate">
            You (<strong>{adminLabel}</strong>) are viewing this account in
            read-only mode. You can browse everything, but changes are
            disabled. Exit impersonation to make edits as yourself.
          </span>
        </div>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleExit}
          loading={exiting}
          className="shrink-0"
        >
          <LogOut size={14} />
          {exiting ? 'Exiting…' : 'Exit Impersonation'}
        </Button>
      </div>
    </div>
  )
}
